import { roundToCurrency } from '../../utils/price';

export interface DiscountLine {
  /**
   * Undiscounted unit amount x quantity. Undefined when the unit carries no
   * amount in the currency being reported, so it cannot be priced.
   */
  gross?: number;
  quantity: number;
}

export interface DistributedAmount {
  /** Discounted unit price. Absent when the line has no known gross. */
  price?: number;
  /** Per-unit discount. Absent when there is none. */
  discount?: number;
}

/**
 * Split an order-level discount across item lines proportionally to gross.
 * GA4 wants a discounted unit price plus a per-unit discount; ARS only reports
 * one order-level figure, so it is apportioned here.
 *
 * The discount is clamped to [0, grossSum] so a price can never go negative.
 * Amounts are rounded to the currency's own precision, so the line sums — which
 * are what `value` reports — can drift from the order total by one minor unit
 * per line.
 */
export function distributeDiscount(
  lines: DiscountLine[],
  totalDiscount: number,
  currency?: string
): DistributedAmount[] {
  const grossSum = lines.reduce((sum, line) => sum + (line.gross ?? 0), 0);
  const effectiveDiscount = Math.min(Math.max(totalDiscount, 0), grossSum);
  const discountShare = grossSum > 0 ? effectiveDiscount / grossSum : 0;

  const perUnit = (total: number, quantity: number): number =>
    quantity > 0 ? roundToCurrency(total / quantity, currency) : 0;

  return lines.map(line => {
    if (line.gross === undefined) return {};
    const lineDiscount = line.gross * discountShare;
    const discount = perUnit(lineDiscount, line.quantity);
    const price = perUnit(line.gross - lineDiscount, line.quantity);
    return discount > 0 ? { price, discount } : { price };
  });
}
